/** @file Animations stage that compiles and caches each animation on its own (per-entity, by identity). */
import { entriesByName } from '@pose2d/format/doc/entriesByName';
import type { CompileStages } from '../compile/compileStages';
import type { CompileCache } from './compileCache';
import { animationDeps } from './animationDeps';
import { canonicalMap } from './canonicalMap';
import { memoStage } from './memoStage';

type AnimationsStage = CompileStages['animations'];

/** Wraps `direct` so an unchanged animation (same object, same animationDeps) reuses its compiled entries. */
export function cachedAnimations(cache: CompileCache, direct: AnimationsStage): AnimationsStage {
  return (doc, index, maps) => {
    const canon = {
      bone: canonicalMap(cache.maps, 'animations.bone', maps.boneIndex),
      slot: canonicalMap(cache.maps, 'animations.slot', maps.slotIndex),
      ik: canonicalMap(cache.maps, 'animations.ik', maps.ikIndex),
      attachment: canonicalMap(cache.maps, 'animations.attachment', maps.attachmentIndex),
      event: canonicalMap(cache.maps, 'animations.event', maps.eventIndex),
    };
    const out: ReturnType<AnimationsStage> = [];
    for (const [id, anim] of entriesByName(doc.animations)) {
      const compiled = memoStage(cache.animations, anim, animationDeps(doc, anim, canon), () =>
        direct({ ...doc, animations: { [id]: anim } }, index, maps));
      for (const entry of compiled) out.push(entry);
    }
    return out;
  };
}
